// DTOs + enums for the Safeguarding "Providers Assets" and "Corporate Funds" pages.
// Both pages share one backend engine (Common/ProviderFunds) parameterised by
// AccountType; the shapes below are common to both app services plus the PA-only
// period-locking service.

export interface ProvidersSummaryInput {
  to: string; // 'YYYY-MM-DD'
}

export interface ProviderDetailsInput {
  providerId: string;
  to?: string | null;
}

// Mirrors ProviderTransactionsInputDto : PaginatedInputDto.
export interface ProviderTransactionsInput {
  providerId: string;
  startDate?: string | null;
  endDate?: string | null;
  type?: string | null;
  skipCount?: number;
  maxResultCount?: number;
  sorting?: string;
}

export interface ProcessProviderTransactionsInput {
  providerId: string;
  to?: string | null;
}

export interface AddManualTransactionInput {
  providerId: string;
  type: number; // ProviderTransactionType int
  balanceTypes: number[]; // BalanceType ints
  amount: number;
  comment: string;
  to: string; // 'YYYY-MM-DD'
}

export interface ResultDto<T = string> {
  value: T;
  text: string;
}

export interface ProvidersSummaryRowDto {
  providerId: string;
  linkedProviders: string[];
  name: string;
  type: string;
  currency: string;
  cashBookBalance: number;
  balance: number;
  availableBalance: number;
  withdrawableBalance: number;
  cashBookBalanceRc: number;
  balanceRc: number;
  discrepancyRc: number;
  availableBalanceRc: number;
  withdrawableBalanceRc: number;
  ownership: string;
  concentration: number;
}

export interface ProviderTransactionRowDto {
  dateOfTransaction: string;
  in: number;
  out: number;
  type: string;
  inRc: number;
  outRc: number;
  cashBookBalance: number;
  cumBalance: number;
  availableBalance: number;
  withdrawableBalance: number;
  cumulativeReservesAccount: number;
  cashBookBalanceRc: number;
  cumBalanceRc: number;
  availableBalanceRc: number;
  withdrawableBalanceRc: number;
  cumulativeReservesAccountRc: number;
  comment: string;
  relatedTransaction: string;
  login: number | null;
  connector: string;
}

export interface ProviderBoxesDto {
  depositCount: number;
  withdrawalsCount: number;
  depositFee: number;
  withdrawalsFee: number;
  reservesCaptured: number;
  excess: number;
}

export interface ProviderFeeValuesDto {
  depositFixedFee: number;
  withdrawalFixedFee: number;
  depositPercentageFee: number;
  withdrawalPercentageFee: number;
  threshold: number | null;
  thresholdMax: number | null;
}

export interface ProviderDetailsDto {
  providerId: string;
  startingBalanceDate: string;
  startingBalance: number;
  currency: string;
  holdingReserves: number;
  rollingDays: number;
  ownership: string;
  processTransaction: boolean;
  lastProcessDate: string;
  lockedThrough: string | null;
  feesType: number; // ConditionalFee int (1 | 2)
  lessThan: ProviderFeeValuesDto;
  betweenThan: ProviderFeeValuesDto;
  greaterThan: ProviderFeeValuesDto;
  feesWithoutCondition: ProviderFeeValuesDto;
}

export interface ManualTransactionResultDto {
  isSuccess: boolean;
  message: string;
}

export interface SyncResponseCode {
  isSuccess: boolean;
  additionalMessage?: string | null;
  errorMessage?: string | null;
}

// --- Period locking (PA-only) ---

export interface ProviderPeriodStatusDto {
  year: number;
  month: number;
  periodMonth: string;
  status: 'Locked' | 'Unlocked' | 'Processing';
  isLocked: boolean;
  isProcessed: boolean;
  cashBookBalanceRc: number | null;
  realCashBalanceRc: number | null;
}

export interface ProviderPeriodLockingViewDto {
  providerId: string;
  providerName: string;
  lockedThrough: string | null;
  months: ProviderPeriodStatusDto[];
}

export interface ProviderPeriodLockLogDto {
  periodMonth: string;
  action: string;
  status: string;
  user: string;
  createdAt: string;
  comment: string;
}

export interface ProviderPeriodActionInput {
  providerId: string;
  year: number;
  month: number;
  comment: string;
}

export interface ProviderPeriodLockResultDto {
  isSuccess: boolean;
  message: string;
  affectedMonths: string[];
}

// --- Enums ---

// Operation type for the manual-transaction card (a 2-item subset).
export const MANUAL_DEPOSIT_VALUE = 7;
export const MANUAL_WITHDRAWAL_VALUE = 8;

// BalanceType enum.
export interface BalanceTypeOption {
  value: number;
  text: string;
  requiresRealCash?: boolean;
}
export const BALANCE_TYPES: BalanceTypeOption[] = [
  { value: 1, text: 'Real Cash Balance', requiresRealCash: true },
  { value: 2, text: 'Available Balance' },
  { value: 3, text: 'Reserves' },
  { value: 4, text: 'Cash Book Balance' },
];

// ConditionalFee enum.
export const CONDITIONAL_FEE = {
  WithConditions: 1,
  WithoutConditions: 2,
} as const;
export const CONDITIONAL_FEE_OPTIONS = [
  { value: 1, text: 'Fees with conditions' },
  { value: 2, text: 'Fees without conditions' },
];
